import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import {useCallback, useContext, useEffect, useRef, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import CodeDiffViewer from "../../../../components/CodeDiffViewer"
import BranchSelector from "../../../../components/Repo/BranchSelectorDropdown"
import {BackendContext} from "../../../../context/BackendContext"
import {ProfileContext} from "../../../../context/Profile.context"
import {UIHelperContext} from "../../../../context/UIHelper.context"
import {notifyUpdate} from "../../../../functions/ipfs-orbit"
import {useGitDiff} from "../../../../hooks/useGitDiff"
import {useGitDir} from "../../../../hooks/useGitDir"
import {PullRequest} from "../../../../models/PullRequest.model"
import {RepoDB} from "../../../../models/RepoDB.model"
import styles from "./index.module.scss"
import {v4 as uuidv4} from "uuid"

interface NewPRProps {
  repoDB?: RepoDB
  branches: string[]
}

export const NewPR = (props: NewPRProps) => {
  const {repoDB, branches} = props
  const {orbitId, repoId} = useParams()
  const navigate = useNavigate()
  const titleRef = useRef<HTMLInputElement>(null)
  const [baseBranch, setBaseBranch] = useState<string>()
  const [mergeBranch, setMergeBranch] = useState<string>()
  const {ipfs, fs} = useContext(BackendContext)
  const {profile} = useContext(ProfileContext)
  const {setSnackMessage} = useContext(UIHelperContext)
  const gitDir = useGitDir(orbitId, repoId)
  const {commits, diffString} = useGitDiff(fs, gitDir, mergeBranch, baseBranch)

  const onCreatePullRequest = useCallback(async () => {
    if (!repoDB || !profile || !baseBranch || !mergeBranch || !ipfs) {
      return
    }

    const pullRequestTitle = titleRef.current?.value
    if (!pullRequestTitle) {
      setSnackMessage("Please enter a title for the pull request")
      return
    }

    const pullRequestID = uuidv4()
    const pullRequest: PullRequest = {
      id: pullRequestID,
      title: pullRequestTitle,
      baseBranch,
      mergeBranch,
      createAt: new Date().getTime(),
      status: "open",
      authorName: profile.get("name"),
      authorProfileAddress: profile.address.toString()
    }

    await repoDB.set(`pullRequest_${pullRequestID}`, pullRequest)
    await notifyUpdate(ipfs, repoDB.address.toString())
    navigate("../")
  }, [baseBranch, mergeBranch, profile, setSnackMessage, navigate, ipfs])

  useEffect(() => {
    setBaseBranch("master")
    const filtered = branches.filter(b => b !== "master")
    if (filtered.length === 0) {
      setMergeBranch("master")
    } else {
      setMergeBranch(filtered[0])
    }
  }, [branches])

  if (!repoDB || !baseBranch || !mergeBranch || branches.length === 0) {
    return null
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        Create a new pull request
      </div>

      <input className={styles.TitleInput}
             ref={titleRef}
             placeholder={"Summarize your pull request"}/>

      <div className={styles.SubHeader}>
        Create a new pull request by comparing changes across two branches
      </div>

      <div className={styles.BranchSection}>
        <BranchSelector
          role="base"
          branches={branches}
          onSelect={setBaseBranch}
          value={baseBranch!}
        />

        <ArrowBackIcon
          fontSize={"small"}
          className={styles.Arrow}
        />

        <BranchSelector
          role="merged"
          branches={branches}
          onSelect={setMergeBranch}
          value={mergeBranch!}
        />

        <button className={styles.CreateButton}
                onClick={onCreatePullRequest}
        >
          Create
        </button>
      </div>

      {diffString && <CodeDiffViewer diffString={diffString}/>}
    </div>
  )
}
